import { NavLink } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@/lib/cn';
import { SearchIcon } from '@/components/layout/icons';
import { Input } from '@/components/ui/input';
import { timeAgo } from '@/lib/format';
import { useMyTransactions } from '@shared/hooks/use-transactions';
import { useMe } from '@shared/hooks/use-me';
import { useTransaction } from '@shared/hooks/use-transaction-actions';
import { StatusBadge } from './status-badge';

export function ChatSidebar({
  selectedId,
  basePath = '/client/chat',
  scope: initialScope,
}: {
  selectedId?: string;
  basePath?: string;
  scope?: 'client' | 'associate';
}) {
  const [activeScope, setActiveScope] = useState<'client' | 'associate' | undefined>(initialScope);
  const { data, isLoading } = useMyTransactions({ pageSize: 50, scope: activeScope });
  const { data: selectedTx } = useTransaction(selectedId);
  const [q, setQ] = useState('');

  const { data: me } = useMe();
  const prevSelectedIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!selectedId || !me?.id || !selectedTx || initialScope !== 'associate') return;
    if (prevSelectedIdRef.current !== selectedId) {
      prevSelectedIdRef.current = selectedId;
      const isTakenTx = selectedTx.clientId === me.id && selectedTx.assignedAssociateId !== me.id;
      if (isTakenTx) {
        setActiveScope('client');
      } else if (selectedTx.assignedAssociateId === me.id) {
        setActiveScope('associate');
      }
    }
  }, [selectedId, me?.id, selectedTx, initialScope]);
  const isAssociateScope = activeScope === 'associate';
  const items = useMemo(() => {
    let list = data?.items ?? [];
    if (isAssociateScope) {
      list = list.filter(
        (t) =>
          (t.associateAssignmentStatus === 'ACCEPTED' || t.status === 'CANCELLED') &&
          (!me?.id || t.assignedAssociateId === me.id),
      );
    }
    const needle = q.trim().toLowerCase();
    return needle
      ? list.filter(
          (t) =>
            t.code.toLowerCase().includes(needle) ||
            t.service.name.toLowerCase().includes(needle) ||
            (isAssociateScope && t.nickName && t.nickName.toLowerCase().includes(needle)),
        )
      : list;
  }, [data, q, isAssociateScope, me?.id]);

  return (
    <aside className="flex h-full w-full flex-col bg-white min-h-0">
      <div className="border-b border-gray-100 p-3 shrink-0">
        <div className="flex items-center justify-between gap-2">
          <h1 className="text-lg font-bold text-gray-900">Chats</h1>
        </div>
        {initialScope === 'associate' && (
          <div className="mt-2 flex gap-1 bg-gray-100 p-0.5 rounded-lg text-xs">
            <button
              type="button"
              onClick={() => setActiveScope('associate')}
              className={cn(
                'flex-1 py-1 rounded-md text-center font-medium transition-all',
                activeScope === 'associate'
                  ? 'bg-white text-brand-700 shadow-xs'
                  : 'text-gray-500 hover:text-gray-900',
              )}
            >
              Assigned
            </button>
            <button
              type="button"
              onClick={() => setActiveScope('client')}
              className={cn(
                'flex-1 py-1 rounded-md text-center font-medium transition-all',
                activeScope === 'client'
                  ? 'bg-white text-brand-700 shadow-xs'
                  : 'text-gray-500 hover:text-gray-900',
              )}
            >
              Taken
            </button>
          </div>
        )}
        <div className="relative mt-2">
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search"
            className="pl-9 text-base md:text-sm"
          />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto min-h-0">
        {isLoading ? (
          <p className="p-4 text-sm text-gray-500">Loading…</p>
        ) : items.length === 0 ? (
          <p className="p-6 text-center text-sm text-gray-500">No transactions yet.</p>
        ) : (
          <ul>
            {items.map((t) => (
              <li key={t.id}>
                <NavLink
                  to={`${basePath}/${t.id}`}
                  className={cn(
                    'flex items-start gap-3 border-l-2 border-transparent px-3 py-3 text-left hover:bg-gray-50 md:py-2.5',
                    selectedId === t.id && 'border-brand-700 bg-brand-50/50',
                  )}
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-xs font-semibold text-brand-700 overflow-hidden border border-gray-100 mt-0.5">
                    {t.service.images && t.service.images[0] ? (
                      <img
                        src={t.service.images[0]}
                        alt={t.service.name}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      t.service.name
                        .split(/\s+/)
                        .map((p) => p[0])
                        .slice(0, 2)
                        .join('')
                        .toUpperCase()
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="truncate text-sm font-semibold text-gray-900 leading-tight">
                        {t.service.name}
                        {isAssociateScope && t.nickName ? (
                          <span className="ml-1.5 font-semibold text-gray-700">({t.nickName})</span>
                        ) : null}
                      </h3>
                      <span className="shrink-0 text-xs text-gray-400 md:text-[11px]">
                        {timeAgo(t.lastMessageAt ?? t.updatedAt)}
                      </span>
                    </div>

                    <div className="mt-0.5 flex items-center gap-1.5 min-w-0">
                      <span className="text-xs font-mono font-medium text-gray-400 md:text-[11px] truncate">
                        #{t.code}
                      </span>
                    </div>

                    <div className="mt-1.5 flex items-center justify-between gap-2">
                      <StatusBadge status={t.status} className="px-2 py-0.5 text-[10px]" />
                      {t.unreadCount > 0 && selectedId !== t.id ? (
                        <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-brand-700 px-1 text-[10px] font-semibold text-white">
                          {t.unreadCount > 99 ? '99+' : t.unreadCount}
                        </span>
                      ) : null}
                    </div>
                  </div>
                </NavLink>
              </li>
            ))}
          </ul>
        )}
      </div>
    </aside>
  );
}
